import { z } from 'zod';
import { VENDOR_PROMOTABLE_CATEGORIES } from '../enums/qualification-category.js';
import { IsoDateStringSchema, UuidV7Schema } from './common.js';

/**
 * Sous-ensemble de catégories qu'une règle d'auto-promotion peut porter —
 * strictement aligné sur ce qu'un admin peut assigner via le picker « Rendre
 * visible au vendor » (`VENDOR_PROMOTABLE_CATEGORIES`). Une règle ne naît que
 * d'une catégorisation admin, donc même domaine de valeurs.
 */
export const VendorPromotableCategorySchema = z.enum(VENDOR_PROMOTABLE_CATEGORIES);

/**
 * Seuil de tentative d'appel d'une règle d'auto-promotion : la règle ne
 * s'applique QU'À PARTIR de cette tentative (`meta.attemptNumber` de la
 * qualification entrante). `null` = aucun seuil (comportement historique).
 *
 * Raison d'être (09/2026) : les codifications de clôture arrivent parfois
 * beaucoup trop tôt du call-center — « injoignable permanent » reçu dès la
 * tentative 1 à 3 alors que la clôture est contractuellement à 10 appels — et
 * refermaient la fiche en Non vente pendant que le centre continuait
 * d'appeler. Sous le seuil, la qualif reste en revue manuelle : visible des
 * seuls admins, et sans effet sur `Lead.status`.
 *
 * Borne haute volontairement large (100) : c'est un garde-fou de saisie, pas
 * une règle métier.
 */
export const MinAttemptNumberSchema = z.number().int().min(1).max(100).nullable();

/** Longueur maximale d'un libellé vendeur (texte court, lu sur mobile). */
export const VENDOR_LABEL_MAX_LENGTH = 60;

/**
 * Libellé vendeur d'une règle (19/10/2026, Cyrille + Valentin) : ce que lisent
 * les RÉSEAUX (non-admins) pour ce type de qualif, dans l'historique de la
 * fiche et la colonne « Dernière qualif — sous-groupe » de l'export.
 *
 * Priorité du texte réseau : libellé vendeur de la règle > sous-groupe Hipto
 * reformulé (`vendorSubMetricLabel`) > libellé de la catégorie. Appliqué À
 * L'AFFICHAGE, jamais recopié dans les events.
 *
 * Saisie : trim, 60 caractères max ; chaîne vide = `null` (effacer → repli).
 */
export const VendorLabelInputSchema = z
  .string()
  .trim()
  .max(VENDOR_LABEL_MAX_LENGTH)
  .transform((v) => (v === '' ? null : v))
  .nullable();

/**
 * Règle d'auto-promotion apprise des décisions admin (table
 * `qualification_auto_rules`). Mappe un type de qualification callcenter
 * (`rawLabel` normalisé) vers la catégorie à auto-appliquer + visibilité
 * vendor. Scopée PAR DELIVERY PARTNER (le client final). La catégorie est
 * OPTIONNELLE : renseignée = auto-promotion ; nulle = type catalogué mais en
 * revue manuelle. Exposée à l'écran admin de gestion des types appris.
 */
export const QualificationAutoRuleSchema = z.object({
  id: UuidV7Schema,
  /** Delivery partner (client final) auquel la règle s'applique. */
  deliveryPartnerId: UuidV7Schema,
  /** Clé de matching : `rawLabel` callcenter normalisé (trim + UPPERCASE). */
  rawLabelKey: z.string(),
  /** Dernier `rawLabel` brut observé, pour affichage lisible. */
  sampleLabel: z.string(),
  /** Catégorie auto-appliquée (nulle = pas de mapping auto, revue manuelle). */
  category: VendorPromotableCategorySchema.nullable(),
  /** Kill-switch : si `false`, retour en revue manuelle pour ce type. */
  autoPromote: z.boolean(),
  /**
   * Seuil de tentative d'appel : la règle ne s'applique qu'à partir de la
   * Nième tentative (`meta.attemptNumber`). `null` = aucun seuil.
   */
  minAttemptNumber: MinAttemptNumberSchema,
  /** Libellé lu par les réseaux (`null` = repli sous-groupe Hipto / catégorie). */
  vendorLabel: z.string().nullable(),
  /** Nombre de qualifs auto-promues par cette règle. */
  appliedCount: z.number().int().min(0),
  /** Admin ayant (re)défini la règle en dernier. */
  lastActorUserId: UuidV7Schema.nullable().optional(),
  createdAt: IsoDateStringSchema,
  updatedAt: IsoDateStringSchema,
});
export type QualificationAutoRule = z.infer<typeof QualificationAutoRuleSchema>;

/**
 * Entrée du CATALOGUE vivant des types de qualification vus par client
 * (`GET /admin/qualification-auto-rules`). Agrège tous les
 * `qualification_received` observés, groupés par `(deliveryPartnerId, rawLabel
 * normalisé)`, joints à la règle éventuelle. Une entrée existe dès qu'un type
 * a été vu, même sans règle (`ruleId` nul, `category` nulle).
 */
export const QualificationTypeCatalogEntrySchema = z.object({
  /** Delivery partner (client final) source de ces qualifs. */
  deliveryPartnerId: UuidV7Schema,
  /** Nom du delivery partner (affichage). */
  deliveryPartnerName: z.string(),
  /** `rawLabel` callcenter normalisé (clé du groupe). */
  rawLabelKey: z.string(),
  /** Un `rawLabel` brut représentatif (le plus récent observé). */
  sampleLabel: z.string(),
  /**
   * Classification CRM amont (hipto_metric) la plus récente non nulle observée
   * pour ce type — indice d'aide à la décision. `null` si le CRM n'a rien mappé.
   */
  metric: z.string().nullable(),
  /** Sous-classification CRM (hipto_sub_metric) associée. `null` si absente. */
  subMetric: z.string().nullable(),
  /** Nombre total de qualifs de ce type pour ce client. */
  seenCount: z.number().int(),
  /** Nombre encore en revue manuelle (`adminOnly=true`). */
  pendingCount: z.number().int(),
  /** Catégorie mappée par la règle (nulle si aucune règle / mapping vide). */
  category: VendorPromotableCategorySchema.nullable(),
  /** Kill-switch de la règle (défaut `true` si aucune règle). */
  autoPromote: z.boolean(),
  /** Seuil de tentative de la règle (`null` = aucun seuil / aucune règle). */
  minAttemptNumber: MinAttemptNumberSchema,
  /** Libellé vendeur de la règle (`null` = aucun / aucune règle). */
  vendorLabel: z.string().nullable(),
  /** Compteur d'applications de la règle (0 si aucune). */
  appliedCount: z.number().int(),
  /** Id de la règle si elle existe, sinon `null`. */
  ruleId: UuidV7Schema.nullable(),
  /** Date de dernière édition de la règle (nulle si aucune). */
  updatedAt: IsoDateStringSchema.nullable(),
});
export type QualificationTypeCatalogEntry = z.infer<typeof QualificationTypeCatalogEntrySchema>;

/**
 * Payload d'upsert d'un mapping depuis l'écran admin (catalogue vivant).
 * Assigner une `category` (non nulle) = auto-promotion ON pour ce
 * `(deliveryPartnerId, rawLabelKey)` + rattrapage du backlog. `category: null`
 * = pas de mapping (le type reste en revue manuelle ; la règle éventuelle est
 * supprimée).
 */
export const UpsertQualificationAutoRuleSchema = z.object({
  deliveryPartnerId: UuidV7Schema,
  rawLabelKey: z.string().min(1),
  sampleLabel: z.string().optional(),
  category: VendorPromotableCategorySchema.nullable(),
  autoPromote: z.boolean().optional(),
  /**
   * Seuil de tentative. `null` explicite = retirer le seuil ; absent =
   * inchangé côté serveur.
   */
  minAttemptNumber: MinAttemptNumberSchema.optional(),
  /**
   * Libellé vendeur. Chaîne vide ou `null` = effacer ; absent = inchangé.
   * Modifier SEULEMENT ce champ ne relance pas le rattrapage du backlog.
   */
  vendorLabel: VendorLabelInputSchema.optional(),
});
export type UpsertQualificationAutoRule = z.infer<typeof UpsertQualificationAutoRuleSchema>;

/**
 * Alias rétro-compatible de l'ancien schéma d'édition. Désormais l'upsert
 * scopé remplace l'ancien PATCH partiel.
 * @deprecated Utiliser `UpsertQualificationAutoRuleSchema`.
 */
export const UpdateQualificationAutoRuleSchema = UpsertQualificationAutoRuleSchema;
export type UpdateQualificationAutoRule = UpsertQualificationAutoRule;
